/**
 * Investment Planning Page - Sprint 16
 * PM-3: Investment planning with budget envelopes
 * 
 * Features:
 * - Financial envelope slider (year/quarter)
 * - Allocation per program/project
 * - Benefit vs Cost visualization
 * - AI recommendation for optimized mix
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DollarSign, Plus, Edit2, Trash2, Loader2, RefreshCw,
  TrendingUp, PieChart, Sliders, Sparkles, Save, X,
  AlertCircle, CheckCircle, ChevronDown, BarChart3, Copy, Download,
  GitCompare, Target
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart, Line
} from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Budget Envelope Slider
const EnvelopeSlider = ({ value, max, onChange, label }) => {
  const percentage = max > 0 ? (value / max * 100) : 0;
  
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between text-sm mb-2">
        <span className="font-medium text-gray-700">{label}</span>
        <span className="text-gray-600">
          ${value?.toLocaleString()} / ${max?.toLocaleString()}
        </span>
      </div>
      <div className="relative">
        <input
          type="range"
          min="0"
          max={max || 1000000}
          value={value || 0}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${percentage}%, #E5E7EB ${percentage}%, #E5E7EB 100%)`
          }}
        />
      </div>
      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>0%</span>
        <span>{percentage.toFixed(0)}% allocated</span>
        <span>100%</span>
      </div>
    </div>
  );
};

// Line Item Row
const LineItemRow = ({ item, onEdit, onDelete }) => {
  const roi = item.amount > 0 ? ((item.expected_benefit / item.amount) * 100 - 100).toFixed(0) : 0;
  
  return (
    <tr className="hover:bg-gray-50 group">
      <td className="px-4 py-3">
        <div>
          <span className="font-medium text-gray-900">{item.target_name || item.target_id}</span>
          <span className="text-xs text-gray-500 ml-2 capitalize">({item.target_type})</span>
        </div>
      </td>
      <td className="px-4 py-3 text-right">
        ${item.amount?.toLocaleString()}
      </td>
      <td className="px-4 py-3 text-right">
        ${item.expected_benefit?.toLocaleString()}
      </td>
      <td className="px-4 py-3 text-right">
        <span className={`px-2 py-0.5 rounded text-xs ${
          roi >= 20 ? 'bg-green-100 text-green-700' :
          roi >= 0 ? 'bg-amber-100 text-amber-700' :
          'bg-red-100 text-red-700'
        }`}>
          {roi}% ROI
        </span>
      </td>
      <td className="px-4 py-3 text-center">
        <div className="flex items-center justify-center gap-1">
          <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-500"
              style={{ width: `${(item.confidence || 0) * 100}%` }}
            />
          </div>
          <span className="text-xs text-gray-500">{((item.confidence || 0) * 100).toFixed(0)}%</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1">
          <button
            onClick={() => onEdit(item)}
            className="p-1 hover:bg-gray-200 rounded"
          >
            <Edit2 className="w-4 h-4 text-gray-500" />
          </button>
          <button
            onClick={() => onDelete(item.line_item_id)}
            className="p-1 hover:bg-red-100 rounded"
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </button>
        </div>
      </td>
    </tr>
  );
};

// AI Recommendation Panel
const AIRecommendationPanel = ({ recommendations, onApply, loading }) => {
  if (loading) {
    return (
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200 p-6">
        <div className="flex items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
          <span className="text-purple-700">AI is analyzing your portfolio...</span>
        </div>
      </div>
    );
  }
  
  if (!recommendations || recommendations.length === 0) {
    return null;
  }
  
  const totalRecommended = recommendations.reduce((sum, r) => sum + r.recommended_amount, 0);
  const totalBenefit = recommendations.reduce((sum, r) => sum + r.expected_benefit, 0);
  
  return (
    <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200 p-6" data-testid="ai-recommendations">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-600" />
          <h3 className="font-semibold text-purple-800">AI Investment Recommendations</h3>
        </div>
        <button
          onClick={() => onApply(recommendations)}
          className="px-4 py-1.5 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 flex items-center gap-2"
          data-testid="apply-ai-btn"
        >
          <CheckCircle className="w-4 h-4" />
          Apply Recommendations
        </button>
      </div>
      
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-white/50 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-purple-700">${totalRecommended.toLocaleString()}</div>
          <div className="text-xs text-purple-600">Total Allocation</div>
        </div>
        <div className="bg-white/50 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-green-700">${totalBenefit.toLocaleString()}</div>
          <div className="text-xs text-green-600">Expected Benefit</div>
        </div>
        <div className="bg-white/50 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-blue-700">
            {totalRecommended > 0 ? ((totalBenefit / totalRecommended) * 100 - 100).toFixed(0) : 0}%
          </div>
          <div className="text-xs text-blue-600">Projected ROI</div>
        </div>
      </div>
      
      <div className="space-y-2">
        {recommendations.map((rec, idx) => (
          <div key={idx} className="flex items-center justify-between bg-white/50 rounded p-2">
            <div>
              <span className="font-medium text-gray-800">{rec.target_name}</span>
              <span className="text-xs text-gray-500 ml-2">({rec.target_type})</span>
            </div>
            <div className="text-right">
              <span className="font-medium text-purple-700">${rec.recommended_amount.toLocaleString()}</span>
              <span className="text-xs text-gray-500 ml-2">({(rec.confidence * 100).toFixed(0)}% conf)</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Main Component
const InvestmentPlanning = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const portfolioId = searchParams.get('portfolio_id');
  const tabParam = searchParams.get('tab');
  
  const [activeTab, setActiveTab] = useState(tabParam || 'plans');
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [portfolios, setPortfolios] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [aiRecommendations, setAiRecommendations] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [selectedPortfolio, setSelectedPortfolio] = useState(portfolioId || '');
  
  // Sync tab state with URL params
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    setSearchParams(newParams);
  };
  
  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedPortfolio) params.append('portfolioId', selectedPortfolio);
      
      const [plansRes, portfoliosRes] = await Promise.all([
        axios.get(`${API_BASE}/portfolio-mgmt/investments?${params}`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/portfolio-mgmt/portfolios`, { headers: getAuthHeaders() })
      ]);
      
      setPlans(plansRes.data.plans || []);
      setPortfolios(portfoliosRes.data.portfolios || []);
      
      // Load programs if portfolio selected
      if (selectedPortfolio) {
        const programsRes = await axios.get(
          `${API_BASE}/portfolio-mgmt/programs?portfolioId=${selectedPortfolio}`,
          { headers: getAuthHeaders() }
        );
        setPrograms(programsRes.data.programs || []);
      }
    } catch (err) {
      console.error('Failed to load investment data:', err);
      toast.error('Failed to load investment plans');
    } finally {
      setLoading(false);
    }
  }, [selectedPortfolio]);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Get AI recommendation
  const handleGetAIRecommendation = async (envelope) => {
    if (!selectedPortfolio) {
      toast.error('Please select a portfolio first');
      return;
    }
    
    setAiLoading(true);
    setAiRecommendations(null);
    
    try {
      const res = await axios.post(
        `${API_BASE}/portfolio-mgmt/investments/ai-recommend`,
        {
          portfolio_id: selectedPortfolio,
          total_envelope: envelope || 1000000,
          optimization_goal: 'balanced'
        },
        { headers: getAuthHeaders() }
      );
      
      setAiRecommendations(res.data.recommendations);
      toast.success('AI recommendations generated');
    } catch (err) {
      toast.error('Failed to get AI recommendations');
    } finally {
      setAiLoading(false);
    }
  };
  
  // Apply AI recommendations
  const handleApplyRecommendations = async (recommendations) => {
    if (!selectedPlan) {
      toast.error('Please select or create a plan first');
      return;
    }
    
    try {
      await axios.post(
        `${API_BASE}/portfolio-mgmt/investments/${selectedPlan.plan_id}/apply-recommendation`,
        recommendations,
        { headers: getAuthHeaders() }
      );
      
      toast.success('Recommendations applied');
      loadData();
      setAiRecommendations(null);
    } catch (err) {
      toast.error('Failed to apply recommendations');
    }
  };
  
  // Create plan
  const handleCreatePlan = async (planData) => {
    try {
      await axios.post(
        `${API_BASE}/portfolio-mgmt/investments`,
        planData,
        { headers: getAuthHeaders() }
      );
      
      toast.success('Investment plan created');
      setShowCreateModal(false);
      loadData();
    } catch (err) {
      toast.error('Failed to create plan');
    }
  };
  
  // Prepare chart data
  const chartData = selectedPlan?.line_items?.map(item => ({
    name: item.target_name || item.target_id?.slice(-6),
    cost: item.amount,
    benefit: item.expected_benefit,
    roi: item.amount > 0 ? ((item.expected_benefit / item.amount - 1) * 100).toFixed(0) : 0
  })) || [];
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-100 p-6" data-testid="investment-planning">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Investment Planning</h1>
          <p className="text-gray-500">Allocate budgets and optimize investments</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedPortfolio}
            onChange={(e) => setSelectedPortfolio(e.target.value)}
            className="border rounded-lg px-3 py-2"
            data-testid="portfolio-selector"
          >
            <option value="">Select Portfolio</option>
            {portfolios.map(p => (
              <option key={p.portfolio_id || p.id} value={p.portfolio_id || p.id}>
                {p.name}
              </option>
            ))}
          </select>
          
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            data-testid="create-plan-btn"
          >
            <Plus className="w-4 h-4" />
            New Plan
          </button>
          
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-white"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Tabs for Plans vs Scenarios */}
      <div className="flex border-b mb-6">
        <button
          onClick={() => handleTabChange('plans')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'plans'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="plans-tab"
        >
          <PieChart className="w-4 h-4 inline mr-2" />
          Investment Plans
        </button>
        <button
          onClick={() => handleTabChange('scenarios')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'scenarios'
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="scenarios-tab"
        >
          <GitCompare className="w-4 h-4 inline mr-2" />
          Scenarios
        </button>
      </div>
      
      {/* Scenarios Tab Content */}
      {activeTab === 'scenarios' ? (
        <ScenariosTab portfolioId={selectedPortfolio} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Plans List */}
        <div className="bg-white rounded-lg border p-4">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <PieChart className="w-5 h-5 text-blue-500" />
            Investment Plans
          </h2>
          
          {plans.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No investment plans yet</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-3 text-blue-600 hover:underline text-sm"
              >
                Create your first plan
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {plans.map(plan => (
                <div
                  key={plan.plan_id}
                  onClick={() => setSelectedPlan(plan)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedPlan?.plan_id === plan.plan_id
                      ? 'border-blue-400 bg-blue-50'
                      : 'hover:border-gray-300'
                  }`}
                  data-testid={`plan-${plan.plan_id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-900">{plan.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      plan.status === 'active' ? 'bg-green-100 text-green-700' :
                      plan.status === 'draft' ? 'bg-gray-100 text-gray-600' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {plan.status}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {plan.scenario} • {plan.period}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    ${plan.metrics?.total_allocated?.toLocaleString()} / ${plan.total_envelope?.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Plan Details & Chart */}
        <div className="lg:col-span-2 space-y-6">
          {/* Envelope Slider */}
          {selectedPlan && (
            <div className="bg-white rounded-lg border p-4">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Sliders className="w-5 h-5 text-purple-500" />
                Budget Envelope
              </h3>
              
              <EnvelopeSlider
                value={selectedPlan.metrics?.total_allocated || 0}
                max={selectedPlan.total_envelope || 1000000}
                onChange={() => {}}
                label={`${selectedPlan.period} Allocation`}
              />
              
              <button
                onClick={() => handleGetAIRecommendation(selectedPlan.total_envelope)}
                disabled={aiLoading}
                className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50"
                data-testid="ai-recommend-btn"
              >
                {aiLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                AI: Recommend Optimized Investment Mix
              </button>
            </div>
          )}
          
          {/* AI Recommendations */}
          <AIRecommendationPanel
            recommendations={aiRecommendations}
            onApply={handleApplyRecommendations}
            loading={aiLoading}
          />
          
          {/* Cost vs Benefit Chart */}
          {selectedPlan && chartData.length > 0 && (
            <div className="bg-white rounded-lg border p-4">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-green-500" />
                Cost vs Benefit Analysis
              </h3>
              
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="cost" name="Investment" fill="#3B82F6" />
                  <Bar dataKey="benefit" name="Expected Benefit" fill="#10B981" />
                  <Line type="monotone" dataKey="roi" name="ROI %" stroke="#8B5CF6" strokeWidth={2} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
          
          {/* Line Items Table */}
          {selectedPlan && (
            <div className="bg-white rounded-lg border overflow-hidden">
              <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Allocation Line Items</h3>
                <span className="text-sm text-gray-500">
                  {selectedPlan.line_items?.length || 0} items
                </span>
              </div>
              
              {selectedPlan.line_items?.length > 0 ? (
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Target</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Benefit</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">ROI</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Confidence</th>
                      <th className="px-4 py-2 w-20"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {selectedPlan.line_items.map((item, idx) => (
                      <LineItemRow
                        key={item.line_item_id || idx}
                        item={item}
                        onEdit={() => {}}
                        onDelete={() => {}}
                      />
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <p>No allocations yet</p>
                  <p className="text-sm mt-1">Use AI recommendations or add items manually</p>
                </div>
              )}
            </div>
          )}
          
          {/* Empty State */}
          {!selectedPlan && (
            <div className="bg-white rounded-lg border p-12 text-center">
              <DollarSign className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-700">Select a Plan</h3>
              <p className="text-gray-500 mt-1">Choose a plan from the list to view details and allocations</p>
            </div>
          )}
        </div>
      </div>
      )}
      
      {/* Create Plan Modal */}
      {showCreateModal && (
        <CreatePlanModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreatePlan}
          portfolios={portfolios}
          selectedPortfolio={selectedPortfolio}
        />
      )}
    </div>
  );
};


// =============================================================================
// SCENARIOS TAB COMPONENT - Sprint 17
// =============================================================================

const ScenariosTab = ({ portfolioId }) => {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedScenarios, setSelectedScenarios] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [aiLoading, setAiLoading] = useState(null);
  
  // Load scenarios
  const loadScenarios = useCallback(async () => {
    if (!portfolioId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/portfolio-mgmt/investments/scenarios?portfolioId=${portfolioId}`, {
        headers: getAuthHeaders()
      });
      setScenarios(res.data.scenarios || []);
    } catch (err) {
      toast.error('Failed to load scenarios');
    } finally {
      setLoading(false);
    }
  }, [portfolioId]);
  
  useEffect(() => {
    loadScenarios();
  }, [loadScenarios]);
  
  // Create scenario
  const handleCreateScenario = async (form, cloneFromId = null) => {
    try {
      const url = cloneFromId 
        ? `${API_BASE}/portfolio-mgmt/investments/scenarios?cloneFrom=${cloneFromId}`
        : `${API_BASE}/portfolio-mgmt/investments/scenarios`;
        
      await axios.post(url, form, { headers: getAuthHeaders() });
      toast.success('Scenario created');
      setShowCreateModal(false);
      loadScenarios();
    } catch (err) {
      toast.error('Failed to create scenario');
    }
  };
  
  // Delete scenario
  const handleDelete = async (scenarioId) => {
    if (!window.confirm('Delete this scenario?')) return;
    try {
      await axios.delete(`${API_BASE}/portfolio-mgmt/investments/scenarios/${scenarioId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Scenario deleted');
      loadScenarios();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };
  
  // Compare scenarios
  const handleCompare = async () => {
    if (selectedScenarios.length < 2) {
      toast.error('Select 2-3 scenarios to compare');
      return;
    }
    
    try {
      const ids = selectedScenarios.map(s => s.scenario_id);
      const res = await axios.post(
        `${API_BASE}/portfolio-mgmt/investments/scenarios/compare?${ids.map(id => `scenario_ids=${id}`).join('&')}`,
        {},
        { headers: getAuthHeaders() }
      );
      setComparison(res.data);
      setShowCompareModal(true);
    } catch (err) {
      toast.error('Failed to compare scenarios');
    }
  };
  
  // AI Optimize
  const handleAIOptimize = async (scenarioId) => {
    setAiLoading(scenarioId);
    try {
      const res = await axios.post(
        `${API_BASE}/portfolio-mgmt/investments/scenarios/${scenarioId}/recommend`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('AI recommendation generated');
      // Could show recommendation in modal
      console.log('AI Recommendation:', res.data);
    } catch (err) {
      toast.error('AI optimization failed');
    } finally {
      setAiLoading(null);
    }
  };
  
  // Export scenario
  const handleExport = async (scenarioId, format = 'csv') => {
    try {
      const res = await axios.get(
        `${API_BASE}/portfolio-mgmt/investments/scenarios/${scenarioId}/export?format=${format}`,
        { headers: getAuthHeaders(), responseType: format === 'csv' ? 'blob' : 'json' }
      );
      
      if (format === 'csv') {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = `scenario_${scenarioId}.csv`;
        a.click();
      }
      toast.success('Export downloaded');
    } catch (err) {
      toast.error('Export failed');
    }
  };
  
  // Toggle selection
  const toggleSelect = (scenario) => {
    if (selectedScenarios.find(s => s.scenario_id === scenario.scenario_id)) {
      setSelectedScenarios(selectedScenarios.filter(s => s.scenario_id !== scenario.scenario_id));
    } else if (selectedScenarios.length < 3) {
      setSelectedScenarios([...selectedScenarios, scenario]);
    } else {
      toast.info('Max 3 scenarios for comparison');
    }
  };
  
  if (!portfolioId) {
    return (
      <div className="bg-white rounded-lg border p-12 text-center">
        <Target className="w-16 h-16 mx-auto mb-4 text-gray-300" />
        <h3 className="text-lg font-medium text-gray-700">Select a Portfolio</h3>
        <p className="text-gray-500 mt-1">Choose a portfolio to manage investment scenarios</p>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Sprint 18: Select All and Financial Constraints Bar */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200 p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={scenarios.length > 0 && selectedScenarios.length === scenarios.length}
                onChange={() => {
                  if (selectedScenarios.length === scenarios.length) {
                    setSelectedScenarios([]);
                  } else {
                    setSelectedScenarios(scenarios.slice(0, 3)); // Max 3 for comparison
                  }
                }}
                className="w-4 h-4 rounded text-purple-600"
                data-testid="select-all-scenarios"
              />
              <span className="font-medium text-purple-700">
                {selectedScenarios.length === scenarios.length ? 'Unselect All' : 'Select All'}
              </span>
            </label>
            {selectedScenarios.length > 0 && (
              <span className="text-sm text-purple-600">{selectedScenarios.length} selected</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* AI High-ROI Subset Suggestion */}
            <button
              onClick={async () => {
                try {
                  const res = await axios.post(
                    `${API_BASE}/portfolio-mgmt/investments/scenarios/ai-suggest-subset`,
                    { portfolio_id: portfolioId, budget_cap: 1000000, min_roi: 10 },
                    { headers: getAuthHeaders() }
                  );
                  toast.success(res.data.message || 'AI suggestion ready');
                  // Could show suggestion modal
                } catch (err) {
                  toast.info('AI suggestion feature coming soon');
                }
              }}
              className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
              data-testid="ai-suggest-subset"
            >
              <Sparkles className="w-4 h-4" />
              AI High-ROI Subset
            </button>
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
          {selectedScenarios.length > 0 && `${selectedScenarios.length} selected`}
        </div>
        <div className="flex items-center gap-3">
          {selectedScenarios.length >= 2 && (
            <button
              onClick={handleCompare}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              data-testid="compare-scenarios-btn"
            >
              <GitCompare className="w-4 h-4" />
              Compare ({selectedScenarios.length})
            </button>
          )}
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            data-testid="create-scenario-btn"
          >
            <Plus className="w-4 h-4" />
            New Scenario
          </button>
        </div>
      </div>
      
      {/* Scenarios Grid */}
      {scenarios.length === 0 ? (
        <div className="bg-white rounded-lg border p-12 text-center">
          <GitCompare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-700">No Scenarios Yet</h3>
          <p className="text-gray-500 mt-1">Create scenarios to model different investment allocations</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create First Scenario
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {scenarios.map(scenario => {
            const isSelected = selectedScenarios.find(s => s.scenario_id === scenario.scenario_id);
            
            return (
              <div
                key={scenario.scenario_id}
                className={`bg-white rounded-lg border p-4 transition-all ${
                  isSelected ? 'border-purple-400 ring-2 ring-purple-200' : 'hover:border-gray-300'
                }`}
                data-testid={`scenario-${scenario.scenario_id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!isSelected}
                        onChange={() => toggleSelect(scenario)}
                        className="w-4 h-4 rounded text-purple-600"
                      />
                      <h3 className="font-semibold text-gray-900">{scenario.name}</h3>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded mt-1 inline-block ${
                      scenario.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {scenario.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleExport(scenario.scenario_id)}
                      className="p-1.5 hover:bg-gray-100 rounded text-gray-500"
                      title="Export CSV"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(scenario.scenario_id)}
                      className="p-1.5 hover:bg-red-100 rounded text-gray-500 hover:text-red-600"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div className="text-sm text-gray-600 mb-3">
                  {scenario.period} {scenario.period_year}
                </div>
                
                {/* Metrics */}
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-gray-50 rounded p-2">
                    <div className="text-gray-500">Cost</div>
                    <div className="font-semibold">${scenario.metrics?.total_allocated?.toLocaleString() || 0}</div>
                  </div>
                  <div className="bg-gray-50 rounded p-2">
                    <div className="text-gray-500">Benefits</div>
                    <div className="font-semibold text-green-600">${scenario.metrics?.total_benefits?.toLocaleString() || 0}</div>
                  </div>
                  <div className="bg-gray-50 rounded p-2">
                    <div className="text-gray-500">ROI</div>
                    <div className="font-semibold">{scenario.metrics?.roi_proxy || 0}%</div>
                  </div>
                  <div className="bg-gray-50 rounded p-2">
                    <div className="text-gray-500">Items</div>
                    <div className="font-semibold">{scenario.metrics?.allocation_count || 0}</div>
                  </div>
                </div>
                
                {/* Constraints */}
                {scenario.constraints && (
                  <div className="mt-3 pt-3 border-t text-xs text-gray-500">
                    Budget Cap: ${scenario.constraints.budget_cap?.toLocaleString() || 'None'} |
                    Risk: {scenario.constraints.risk_tolerance || 'medium'}
                  </div>
                )}
                
                {/* AI Optimize Button */}
                <button
                  onClick={() => handleAIOptimize(scenario.scenario_id)}
                  disabled={aiLoading === scenario.scenario_id}
                  className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white text-sm rounded-lg hover:from-purple-600 hover:to-blue-600 disabled:opacity-50"
                >
                  {aiLoading === scenario.scenario_id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  AI Optimize
                </button>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Create Scenario Modal */}
      {showCreateModal && (
        <CreateScenarioModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateScenario}
          portfolioId={portfolioId}
          existingScenarios={scenarios}
        />
      )}
      
      {/* Compare Modal */}
      {showCompareModal && comparison && (
        <CompareModal
          isOpen={showCompareModal}
          onClose={() => setShowCompareModal(false)}
          comparison={comparison}
        />
      )}
    </div>
  );
};

// Create Scenario Modal
const CreateScenarioModal = ({ isOpen, onClose, onSubmit, portfolioId, existingScenarios }) => {
  const [form, setForm] = useState({
    name: '',
    portfolio_id: portfolioId,
    period: 'annual',
    constraints: {
      budget_cap: 1000000,
      min_benefits: 0,
      risk_tolerance: 'medium'
    }
  });
  const [cloneFrom, setCloneFrom] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast.error('Scenario name required');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(form, cloneFrom || null);
    } finally {
      setSubmitting(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Create Investment Scenario</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Scenario Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Aggressive Growth"
              className="w-full border rounded px-3 py-2 mt-1"
              required
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700">Clone From (Optional)</label>
            <select
              value={cloneFrom}
              onChange={(e) => setCloneFrom(e.target.value)}
              className="w-full border rounded px-3 py-2 mt-1"
            >
              <option value="">Start Fresh</option>
              {existingScenarios.map(s => (
                <option key={s.scenario_id} value={s.scenario_id}>{s.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700">Period</label>
            <select
              value={form.period}
              onChange={(e) => setForm({ ...form, period: e.target.value })}
              className="w-full border rounded px-3 py-2 mt-1"
            >
              <option value="annual">Annual</option>
              <option value="Q1">Q1</option>
              <option value="Q2">Q2</option>
              <option value="Q3">Q3</option>
              <option value="Q4">Q4</option>
            </select>
          </div>
          
          <div className="border-t pt-4">
            <h3 className="font-medium text-gray-700 mb-3">Constraints</h3>
            
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-600">Budget Cap ($)</label>
                <input
                  type="number"
                  value={form.constraints.budget_cap}
                  onChange={(e) => setForm({
                    ...form,
                    constraints: { ...form.constraints, budget_cap: parseFloat(e.target.value) }
                  })}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-600">Minimum Benefits ($)</label>
                <input
                  type="number"
                  value={form.constraints.min_benefits}
                  onChange={(e) => setForm({
                    ...form,
                    constraints: { ...form.constraints, min_benefits: parseFloat(e.target.value) }
                  })}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-600">Risk Tolerance</label>
                <select
                  value={form.constraints.risk_tolerance}
                  onChange={(e) => setForm({
                    ...form,
                    constraints: { ...form.constraints, risk_tolerance: e.target.value }
                  })}
                  className="w-full border rounded px-3 py-2 mt-1"
                >
                  <option value="low">Low - Conservative</option>
                  <option value="medium">Medium - Balanced</option>
                  <option value="high">High - Aggressive</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2 border rounded font-medium hover:bg-gray-50">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-2 bg-purple-600 text-white rounded font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Create Scenario
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Compare Modal
const CompareModal = ({ isOpen, onClose, comparison }) => {
  if (!isOpen || !comparison) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[80vh] overflow-auto shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <GitCompare className="w-5 h-5" />
            Scenario Comparison
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="grid grid-cols-3 gap-4 mb-6">
          {comparison.scenarios?.map((scenario, idx) => (
            <div key={scenario.scenario_id} className={`rounded-lg p-4 ${
              idx === 0 ? 'bg-blue-50' : idx === 1 ? 'bg-purple-50' : 'bg-green-50'
            }`}>
              <h3 className="font-semibold mb-2">{scenario.name}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Cost:</span>
                  <span className="font-medium">${scenario.metrics?.total_cost?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Benefits:</span>
                  <span className="font-medium text-green-600">${scenario.metrics?.total_benefits?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">ROI:</span>
                  <span className="font-medium">{scenario.metrics?.roi_proxy}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Allocations:</span>
                  <span className="font-medium">{scenario.metrics?.allocation_count}</span>
                </div>
              </div>
              
              {scenario.constraints_satisfied && (
                <div className="mt-3 pt-3 border-t">
                  <div className="text-xs text-gray-500 mb-1">Constraints:</div>
                  <div className="flex flex-wrap gap-1">
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      scenario.constraints_satisfied.budget_cap ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      Budget {scenario.constraints_satisfied.budget_cap ? '✓' : '✗'}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      scenario.constraints_satisfied.min_benefits ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      Benefits {scenario.constraints_satisfied.min_benefits ? '✓' : '✗'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        
        {comparison.deltas?.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-700 mb-2">Deltas</h4>
            <div className="grid grid-cols-3 gap-4 text-sm">
              {comparison.deltas.map((delta, idx) => (
                <div key={idx}>
                  <div className="text-gray-500 text-xs mb-1">
                    {idx === 0 ? '1st → 2nd' : '2nd → 3rd'}
                  </div>
                  <div className={`${delta.cost_delta > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    Cost: {delta.cost_delta > 0 ? '+' : ''}${delta.cost_delta?.toLocaleString()}
                  </div>
                  <div className={`${delta.benefits_delta > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    Benefits: {delta.benefits_delta > 0 ? '+' : ''}${delta.benefits_delta?.toLocaleString()}
                  </div>
                  <div>
                    ROI: {delta.roi_delta > 0 ? '+' : ''}{delta.roi_delta?.toFixed(1)}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="flex justify-end mt-4">
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};



// Create Plan Modal
const CreatePlanModal = ({ isOpen, onClose, onSubmit, portfolios, selectedPortfolio }) => {
  const [form, setForm] = useState({
    name: '',
    portfolio_id: selectedPortfolio || '',
    scenario: 'baseline',
    period: 'annual',
    total_envelope: 1000000,
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.portfolio_id) {
      toast.error('Please fill required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await onSubmit(form);
    } finally {
      setSubmitting(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Create Investment Plan</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Plan Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="FY26 Investment Plan"
              className="w-full border rounded px-3 py-2 mt-1"
              required
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700">Portfolio *</label>
            <select
              value={form.portfolio_id}
              onChange={(e) => setForm({ ...form, portfolio_id: e.target.value })}
              className="w-full border rounded px-3 py-2 mt-1"
              required
            >
              <option value="">Select portfolio...</option>
              {portfolios.map(p => (
                <option key={p.portfolio_id || p.id} value={p.portfolio_id || p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Scenario</label>
              <select
                value={form.scenario}
                onChange={(e) => setForm({ ...form, scenario: e.target.value })}
                className="w-full border rounded px-3 py-2 mt-1"
              >
                <option value="baseline">Baseline</option>
                <option value="optimistic">Optimistic</option>
                <option value="conservative">Conservative</option>
              </select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Period</label>
              <select
                value={form.period}
                onChange={(e) => setForm({ ...form, period: e.target.value })}
                className="w-full border rounded px-3 py-2 mt-1"
              >
                <option value="annual">Annual</option>
                <option value="Q1">Q1</option>
                <option value="Q2">Q2</option>
                <option value="Q3">Q3</option>
                <option value="Q4">Q4</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700">Total Envelope ($)</label>
            <input
              type="number"
              value={form.total_envelope}
              onChange={(e) => setForm({ ...form, total_envelope: parseFloat(e.target.value) })}
              className="w-full border rounded px-3 py-2 mt-1"
              min="0"
              step="10000"
            />
          </div>
          
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 border rounded font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-2 bg-blue-600 text-white rounded font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Create Plan
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvestmentPlanning;
